import React, { useState, useCallback, useEffect } from 'react';
import Styles from './NewUserSubscriptionPlan.module.scss';
import Header from 'Components/Header/Header';
import LandingLayout from 'layouts/LandingLayout/LandingLayout';
import { SUBSCRIPTION } from 'constants.jsx';
// import { SubscriptionCard } from './SubscriptionCard';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setLoader } from 'redux/slices/loader';
import { getPlans } from 'services/subscriptionService';
import { sortArrayCustomOrder } from 'helpers';
import SubscriptionCard from 'Containers/Subscription/SubscriptionCard';
import PricingHero from 'Components/Pages/PricingPlans/PricingHero';
import PricingHeader from 'Components/Pages/PricingPlans/Header';

const SubscriptionCardContainer = () => {
  const [currentTab, setCurrentTab] = useState(2); // Changed from 1 to 2 to default to yearly
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Mock data as fallback - matching the exact structure expected by PricingTable
  const mockPlans = [
    {
      id: 'basic_plan_monthly',
      type: 'Basic Plan',
      amount: 20,
      old_amount: 20,
      interval: 'month',
      services: [
        'AI-powered Topic Ideation',
        'AI-powered Content Planning',
        'Access to AI Templates',
        'Smart Entry Editor',
        'AI Editing Features',
      ],
    },
    {
      id: 'pro_plan_monthly',
      type: 'Pro Plan',
      amount: 55,
      old_amount: 55,
      interval: 'month',
      services: [
        'Comes with 1 Seat (but can add up to 2 seats for $45/mo per seat)',
        'AI-powered projects with automated content planning',
        'SEO research tool',
        'Integration with up to 5 channels',
        'Brand Voice',
        'Direct scheduling and publishing to connected channels',
        'Multi-model prompting (Claude, GPT, Gemini) in smart entry editor',
        'Collaboration features',
      ],
    },
    {
      id: 'business_plan_monthly',
      type: 'Business Plan',
      amount: 79,
      old_amount: 79,
      interval: 'month',
      services: [
        '5 user seats (additional seats available at $35/mo per seat)',
        'Review and approval workflow features',
        'Advanced analytics and reporting',
        'AI-powered content strategy',
      ],
    },
  ];

  const mockYearlyPlans = [
    {
      id: 'basic_plan_yearly',
      type: 'Basic Plan',
      amount: 14,
      old_amount: 20,
      interval: 'year',
      services: [
        'AI-powered Topic Ideation',
        'AI-powered Content Planning',
        'Access to AI Templates',
        'Smart Entry Editor',
        'AI Editing Features',
      ],
    },
    {
      id: 'pro_plan_yearly',
      type: 'Pro Plan',
      amount: 38,
      old_amount: 55,
      interval: 'year',
      services: [
        'Comes with 1 Seat (but can add up to 2 seats for $45/mo per seat)',
        'AI-powered projects with automated content planning',
        'SEO research tool',
        'Integration with up to 5 channels',
        'Brand Voice',
        'Direct scheduling and publishing to connected channels',
        'Multi-model prompting (Claude, GPT, Gemini) in smart entry editor',
        'Collaboration features',
      ],
    },
    {
      id: 'business_plan_yearly',
      type: 'Business Plan',
      amount: 55,
      old_amount: 79,
      interval: 'year',
      services: [
        '5 user seats (additional seats available at $35/mo per seat)',
        'Review and approval workflow features',
        'Advanced analytics and reporting',
        'AI-powered content strategy',
      ],
    },
  ];

  const fetchPlans = useCallback(async () => {
    try {
      dispatch(
        setLoader({
          isLoading: true,
          heading: 'Loading Plans',
          description: 'Please wait...',
        }),
      );
      const plansRes = await getPlans();
      const { data } = plansRes || {};

      const { products } = data || {};
      const { stripe_products: stripeProducts } = products || {};

      console.log('Raw stripe products:', stripeProducts);

      if (stripeProducts && stripeProducts.length > 0) {
        const transformedStripeProducts = stripeProducts.reduce(
          (acc, stripeProduct) => {
            const { stripe_prices, name, description, additional_info } =
              stripeProduct || {};
            const monthlyPlan = stripe_prices.find(
              (stripePrice) =>
                stripePrice.interval === 'month' && stripePrice.active === true,
            );
            const yearlyPlan = stripe_prices.find(
              (stripePrice) =>
                stripePrice.interval === 'year' && stripePrice.active === true,
            );
            if (acc.monthlyPlan) {
              acc.monthlyPlan = [
                ...acc.monthlyPlan,
                { ...monthlyPlan, type: name, description, additional_info },
              ];
            } else {
              acc.monthlyPlan = [
                { ...monthlyPlan, type: name, description, additional_info },
              ];
            }

            if (acc.annualplan) {
              acc.annualplan = [
                ...acc.annualplan,
                { ...yearlyPlan, type: name, description, additional_info },
              ];
            } else {
              acc.annualplan = [
                { ...yearlyPlan, type: name, description, additional_info },
              ];
            }
            acc.monthlyPlan = sortArrayCustomOrder(acc.monthlyPlan);
            acc.annualplan = sortArrayCustomOrder(acc.annualplan);

            return acc;
          },
          {},
        );

        console.log('Transformed plans:', transformedStripeProducts);
        setSubscriptionPlans(transformedStripeProducts);
      } else {
        console.log('No stripe products found, using mock data');
        // Use mock data if API doesn't return data
        setSubscriptionPlans({
          monthlyPlan: mockPlans,
          annualplan: mockYearlyPlans,
        });
      }
      dispatch(setLoader({ isLoading: false }));
    } catch (e) {
      console.error('Error fetching plans:', e);
      console.log('Using mock data due to API error');
      // Use mock data on error
      setSubscriptionPlans({
        monthlyPlan: mockPlans,
        annualplan: mockYearlyPlans,
      });
      dispatch(setLoader({ isLoading: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    if (user && navigate) {
      navigate(`/${SUBSCRIPTION}`);
      navigate(`/`);
    }
  }, [user, navigate]);

  const renderTab = useCallback(() => {
    const currentPlans =
      currentTab === 1
        ? subscriptionPlans.monthlyPlan
        : subscriptionPlans.annualplan;
    console.log('Current tab:', currentTab);
    console.log('Current plans:', currentPlans);

    switch (currentTab) {
      case 1:
        return (
          <SubscriptionCard
            plan={subscriptionPlans.monthlyPlan}
            newUser={true}
            useTableLayout={true}
          />
        );
      case 2:
        return (
          <SubscriptionCard
            plan={subscriptionPlans.annualplan}
            newUser={true}
            useTableLayout={true}
          />
        );
      default:
        return <h1>Something went wrong</h1>;
    }
  }, [currentTab, subscriptionPlans]);

  const handleToggleClick = () => {
    // Toggle between tabs based on the current tab
    const newTabId = currentTab === 1 ? 2 : 1;
    setCurrentTab(newTabId);
  };

  return (
    <LandingLayout>
      <div className={Styles.container}>
        <Header />

        {/* New Hero Section */}
        <PricingHero />

        {/* Pricing Plans Header */}
        <div className={Styles.pricingHeader}>
          <PricingHeader
            isChecked={currentTab === 2}
            handleToggleClick={handleToggleClick}
          />
        </div>
      </div>

      {/* Pricing Table - rendered outside the styled container */}
      <div className={Styles.pricingTableContainer}>{renderTab()}</div>
    </LandingLayout>
  );
};

export default SubscriptionCardContainer;
